import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from "react";
import { api } from "./api";

const SectorContext = createContext(null);
const STORAGE_KEY = "rbt.sector.v1";
const DEFAULT_SECTOR = "generic";

export function SectorProvider({ children }) {
    const [sectorId, setSectorIdState] = useState(() => {
        // Sector preference is a non-sensitive UI choice (akin to dark mode).
        // localStorage is the right surface; never store credentials or PII here.
        try {
            return localStorage.getItem(STORAGE_KEY) || DEFAULT_SECTOR;
        } catch (e) {
            console.warn("sector: localStorage read failed", e);
            return DEFAULT_SECTOR;
        }
    });
    const [sectors, setSectors] = useState([]);
    const [disclaimer, setDisclaimer] = useState("");

    useEffect(() => {
        api.get("/sectors")
            .then((r) => {
                setSectors(r.data.sectors || []);
                setDisclaimer(r.data.disclaimer || "");
            })
            .catch(() => setSectors([]));
    }, []);

    const setSectorId = useCallback((id) => {
        const next = id || DEFAULT_SECTOR;
        setSectorIdState(next);
        try {
            localStorage.setItem(STORAGE_KEY, next);
        } catch (e) {
            console.warn("sector: localStorage write failed", e);
        }
    }, []);

    const current = useMemo(
        () => sectors.find((s) => s.id === sectorId) || { id: sectorId, label: sectorId },
        [sectors, sectorId],
    );

    const value = useMemo(
        () => ({ sectorId, current, sectors, disclaimer, setSectorId }),
        [sectorId, current, sectors, disclaimer, setSectorId],
    );

    return (
        <SectorContext.Provider value={value}>{children}</SectorContext.Provider>
    );
}

export function useSector() {
    const ctx = useContext(SectorContext);
    if (!ctx) throw new Error("useSector must be used inside SectorProvider");
    return ctx;
}
